<script lang="ts">
    import { Component, mixins, Prop, Watch } from 'vue-facing-decorator';
    import EventPresentationComponent from "@/components/ticket-selection/EventPresentation.vue";
    import {
        EventType,
        panelStateService,
        PanelType,
        PriceLevelFilterState,
        TicketSelectionApplicationData,
        useSeatmapStore,
    } from "@/domain/ticket-selection";
    import ModeSelectionComponent from '@/components/ticket-selection/ModeSelection.vue';
    import EventPromotionComponent from "@/components/ticket-selection/EventPromotion.vue";
    import BestSeatSelectionComponent from "@/components/ticket-selection/best-seat-selection/BestSeatSelection.vue";
    import SeatmapComponent from "@/components/ticket-selection/seatmap/Seatmap.vue";
    import { PerformanceQASelectors, TranslationKey } from "webshop-modules";
    import Panel from "@/components/molecules/panel/Panel.vue";
    import { useBestSeatStore } from "@/domain/ticket-selection/application/BestSeat.store";
    import { useRecaptchaStore } from "@/domain/search/application/Recaptcha.store";
    import vueRecaptcha from 'vue3-recaptcha2';
    import { BreakpointHandler, TicketSelectionDisplay } from "@/mixins";
    import BottomPanel from "@/components/molecules/panel/bottom-panel/BottomPanel.vue";
    import EventInformation from "@/components/ticket-selection/EventInformation.vue";
    import PriceLevelFilter from "@/components/ticket-selection/seatmap/PriceLevelFilter.vue";
    import { Stay22Helper } from "extras";
    import PromotionPanel from "@/components/ticket-selection/seatmap/PromotionPanel.vue";
    import SelectionComponent from "@/components/ticket-selection/seatmap/Selection.vue";

@Component({
    name: 'TicketSelection',
    components: {
        SelectionComponent,
        PromotionPanel,
        PriceLevelFilter,
        EventInformation,
        BottomPanel,
        SeatmapComponent,
        BestSeatSelectionComponent,
        EventPromotionComponent,
        ModeSelectionComponent,
        EventPresentationComponent,
        Panel,
        vueRecaptcha,
    },
})
export default class TicketSelection extends mixins(TicketSelectionDisplay, BreakpointHandler) {

    @Prop() readonly data!: TicketSelectionApplicationData;

    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly EventType: typeof EventType = EventType;
    public readonly PanelType: typeof PanelType = PanelType;
    public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;
    public readonly panelState = panelStateService;

    public stay22InnerHTML: string | undefined = '';
    public selectedPriceFilter: PriceLevelFilterState | null = null;
    public triggerPanelDestroy = false;

    private readonly bestSeatStore = useBestSeatStore();
    private readonly recaptchaStore = useRecaptchaStore();
    public readonly seatmapStore = useSeatmapStore();

    @Watch('displayStateStore.isFullScreenSeatmap')
    onFullScreenSeatmapChange(val: boolean, oldVal: boolean) {
        const body: HTMLElement = document.querySelector('body') as HTMLElement;

        if (val && !body.classList.contains('is-standalone-app')) {
            body.classList.add('is-standalone-app');
        }

        if (!val) {
            body.classList.remove('is-standalone-app');
        }

        this.promotionStore.changeHeaderDisplay(!val);
    }

    @Watch('displayStateStore.modeSelectionState.bestSeat')
    onModeSelectionChange(isBestSeatSelected: boolean, oldVal: boolean) {
        if (!isBestSeatSelected) {
            return;
        }

        this.toggleAdditionalInformationVisibilityTo(true);
    }

    @Watch('displayStateStore.isSeatmapDisplayed')
    private onSeatmapDisplayChange(isDisplayed: boolean, oldVal: boolean) {
        if (!this.displayStateStore.isSeatmapDisplayed) {
            this.selectedPriceFilter = null;
        }
    }

    @Watch('panelState.activePanel')
    private onPanelChange(activePanel: PanelType | null): void {
        if (activePanel === null) {
            this.triggerPanelDestroy = !this.triggerPanelDestroy;
        }
    }

    public get canShowPromotion(): boolean {
        const isPromotionAvailable = this.promotionStore !== null
            && this.promotionStore.availablePromotions
            && !!this.promotionStore.availablePromotions.length
            && this.displayStateStore.canShowSelection;

        return isPromotionAvailable && (!this.displayStateStore.isSeatmapDisplayed || !this.isMobile);
    }

    public collapseHeader(): void {
        this.displayStateStore.updateDisplayModeWith({
            bestSeat: false,
            seatmap: {
                isSeatmapVisible: true,
                isMenuExpanded: false,
            }
        })
    }

    public canDisplayWarning(): boolean {
        return !!(!this.displayStateStore.getPriceList
        || !this.displayStateStore.getPriceList!.priceLevels?.length
        || this.displayStateStore.hasDoubleCurrency) && !this.displayStateStore.isSeatmapDisplayed;
    }

    private created() {
        if (!this.data) {
            return;
        }

        this.stay22InnerHTML = Stay22Helper.getStay22InnerHTML();
        this.bestSeatStore.initializeStore(this.data);
        this.displayStateStore.initializeStore(this.data);

        if (this.data && this.data.priceList && this.data.priceList.promotions) {
            this.promotionStore.initializePromotions(this.data.priceList.promotions);
        }

        if (this.data.configuration.reCaptcha.key) {
            this.recaptchaStore.setRequired(true);
        }
    }

    private isMobileMenuOpen() {
        return this.isMobile && this.displayStateStore.modeSelectionState.seatmap.isMenuExpanded;
    }

    protected mounted(): void {
        if (this.recaptchaStore.isRequired()) {
            this.recaptchaStore.initialize(this.$refs['vueRecaptcha']);
        }
    }
}
</script>

<template>
    <div class="p-ticketSelection__header l-container"
         v-bind:class="{'-absolute body__no-scroll': isMobile && displayModeState.seatmap.isSeatmapVisible}">
        <EventPresentationComponent
            v-show="true"
            :event-information="data.eventInformation">
        </EventPresentationComponent>
        <ModeSelectionComponent
            v-if="displayStateStore.hasSeatmapConfig && displayStateStore.canShowSelection"
            v-on:activate-seatmap="displayStateStore.updateDisplayModeWith"
            v-on:activate-bestseat="displayStateStore.updateDisplayModeWith"
            v-on:toggle-event-header-expansion="displayStateStore.updateDisplayModeWith">
        </ModeSelectionComponent>
    </div>

    <div class="p-ticketSelection__content l-container l-bg-primary">
        <transition name="fade">
            <div v-if="isMobileMenuOpen()" v-on:click="collapseHeader" class="p-ticketSelection__menuOverlay"></div>
        </transition>

        <EventPromotionComponent
            v-if="canShowPromotion && displayStateStore.modeSelectionState.bestSeat"
            :configuration="data.configuration">
        </EventPromotionComponent>

        <vue-recaptcha v-if="recaptchaStore.isRequired()"
                       :sitekey="data.configuration.reCaptcha.key"
                       size="invisible"
                       @verify="recaptchaStore.handleVerifiedEvent"
                       @expire="recaptchaStore.handleExpiredEvent"
                       ref="vueRecaptcha">
        </vue-recaptcha>

        <template v-if="displayStateStore.canShowSelection">
            <BestSeatSelectionComponent
                v-if="displayStateStore.getPriceList"
                v-show="displayModeState.bestSeat || !displayStateStore.hasSeatmapConfig"
                :data="displayStateStore.getPriceList.priceLevels"
                :currencyOptions="data.currencyOptions"
                :eventName="data.eventInformation.eventName">
            </BestSeatSelectionComponent>

            <div id="seatmap-wrapper"
                 class="p-ticketSelection__seatmapContainer"
                 v-show="displayModeState.seatmap.isSeatmapVisible"
                :data-qa="PerformanceQASelectors.SEATMAP_WRAPPER">
                <SeatmapComponent :data="data" :selectedFilter="selectedPriceFilter"></SeatmapComponent>
            </div>
        </template>

        <template v-if="canDisplayWarning()">
            <div class="m-section">
                <div class="m-blockNotice -warning">
                    <div class="m-blockNotice__content">
                        <p v-if="!displayStateStore.getPriceList || !displayStateStore.getPriceList.priceLevels.length"
                           v-translate="{ key: Translation.Notice_prices_not_available }"></p>
                        <p v-else-if="displayStateStore.hasDoubleCurrency"
                           v-translate="{ key: Translation.Notice_double_currency }"></p>
                    </div>
                </div>
            </div>
        </template>

        <event-information :data="data"></event-information>
    </div>

    <bottom-panel v-if="isMobile && panelState.isPanelOpen && displayModeState.seatmap.isSeatmapVisible"
                  :title="panelState.activePanel"
                  :trigger-destroy-panel="triggerPanelDestroy"
                  v-on:destroyPanel="panelState.closePanel()">
        <template v-if="panelState.activePanel === PanelType.INFO">
            <event-information :data="data"></event-information>
            <div v-html="stay22InnerHTML"></div>
        </template>

        <div v-else-if="panelState.activePanel === PanelType.PROMO">
            <PromotionPanel :event-id="data.configuration.eventId"></PromotionPanel>
        </div>

        <div v-else-if="panelState.activePanel === PanelType.FILTER">
            <price-level-filter :data="data"></price-level-filter>
        </div>

        <div v-else-if="panelState.activePanel === PanelType.SELECTION">
            <SelectionComponent :data="data"></SelectionComponent>
        </div>
    </bottom-panel>
</template>

<style scoped>
    .fade-enter-active, .fade-leave-active {
        transition: opacity 0.3s;
    }

    .fade-enter-from, .fade-leave-to {
        opacity: 0;
    }

    .fade-enter-to, .fade-leave-from {
        opacity: 1;
    }
</style>
